import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export interface DmReplyChannel {
  channelId: number;
  threadId: number | null;
  platform: string;
  senderDisplayName: string | null;
  windowExpiresAt: string | null;
  hoursRemaining: number | null;
  eligible: boolean;
  closeToExpiring: boolean;
  reason: 'no_thread' | 'no_inbound_yet' | 'window_expired' | null;
}

export function useDmReplyChannels(contactId: number) {
  return useQuery<{ channels: DmReplyChannel[] }>({
    queryKey: ['dm-reply-channels', contactId],
    queryFn: () => api.get<{ channels: DmReplyChannel[] }>(`/api/contacts/${contactId}/dm-reply-channels`),
    staleTime: 30_000,
  });
}

export function useCreateDmDraft(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { channelId: number; threadId: number; body: string }) =>
      api.post<{ id: number }>(`/api/contacts/${contactId}/dm-reply`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-timeline', contactId] }),
  });
}

export function useSendDmDraft(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: number) =>
      api.post<{ ok: boolean; error?: string; platformMessageId?: string }>(`/api/contacts/${contactId}/dm-reply/${messageId}/send`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contact-timeline', contactId] });
      void qc.invalidateQueries({ queryKey: ['dm-reply-channels', contactId] });
    },
  });
}

export function useCancelDmDraft(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: number) =>
      api.post<{ ok: boolean }>(`/api/contacts/${contactId}/dm-reply/${messageId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-timeline', contactId] }),
  });
}
